/**
 * Move-to-workspace helpers for the dashboard three-dot menu.
 *
 * Lives in its own module so the optimistic-update + Supabase-update
 * behaviour is testable in isolation, without rendering the full dashboard
 * tree.
 *
 * Phase A naming contract: the DB column is `brand_id`. The user-facing
 * label is "workspace". Phase B will rename the column.
 *
 * @module components/workspace/moveToWorkspace
 */

import type { Workspace } from '../../lib/org-types';

/**
 * A single "Move to <workspace>" submenu entry. The dashboard maps this onto
 * its DropdownMenuItem shape (label/onClick/icon) but keeps the move payload
 * separate so tests can assert the content without re-rendering React.
 */
export interface MoveToWorkspaceItem {
  /** Target workspace id (`brands.id`). */
  id: string;
  /** Display label - the workspace name. */
  label: string;
  /** Optional accent color from the workspace, used by the renderer for a
   *  small color dot consistent with the switcher chip. */
  color: string | null;
}

/**
 * Build the list of "Move to <workspace>" entries for a campaign menu.
 *
 * @param activeWorkspaceId Currently active workspace id (excluded from result).
 * @param workspaces        All workspaces in the active org.
 * @returns Submenu entries for every other workspace, in input order.
 */
export function buildMoveToWorkspaceItems(
  activeWorkspaceId: string,
  workspaces: Workspace[],
): MoveToWorkspaceItem[] {
  return workspaces
    .filter((w) => w.id !== activeWorkspaceId)
    .map((w) => ({ id: w.id, label: w.name, color: w.color }));
}

/**
 * Minimal supabase-shaped surface accepted by `moveSessionToWorkspace`. Both
 * the production Supabase JS client and a hand-rolled test stub satisfy this
 * shape, so we avoid a hard dependency on `@supabase/supabase-js` at the
 * call site.
 */
export interface MoveToWorkspaceSupabase {
  from: (table: string) => {
    update: (patch: Record<string, unknown>) => {
      eq: (column: string, value: string) => PromiseLike<{ error: { message: string; code?: string } | null }>;
    };
  };
}

/** Safe action-scoped failure; dependency details remain available as the cause. */
export class WorkspaceMoveError extends Error {
  constructor(readonly code: 'workspace_asset_mismatch' | 'unknown', cause: unknown) {
    super(code === 'workspace_asset_mismatch'
      ? 'Conversations with uploaded question images must stay in their current workspace.'
      : 'Could not move this conversation.', { cause });
    this.name = 'WorkspaceMoveError';
  }
}

/**
 * Whether the database refused a move because the conversation's uploaded
 * images belong to its current workspace. Image questions and frozen response
 * schemas raise `H0001`; the definition guard checks uploaded information
 * images first and raises `22023` with this exact resource message.
 *
 * @param error Supabase error from the `brand_id` update.
 */
function isWorkspaceAssetError(error: { message: string; code?: string }): boolean {
  return error.code === 'H0001'
    || (error.code === '22023' && error.message === 'invalid_multimodal_resource: image');
}

/**
 * Move an interview session (conversation/campaign) to a different workspace by
 * updating its `brand_id`. Phase A column name; Phase B renames to
 * `workspace_id`. Throws on Supabase error so the caller can capture to
 * PostHog and roll back any optimistic UI update.
 *
 * @param supabase   Supabase client (provided by the caller - kept generic so
 *                   tests can pass a stub).
 * @param sessionId  `interview_sessions.id` of the row to move.
 * @param targetId   Destination workspace id (`brands.id`).
 */
export async function moveSessionToWorkspace(
  supabase: MoveToWorkspaceSupabase,
  sessionId: string,
  targetId: string,
): Promise<void> {
  const { error } = await supabase
    .from('interview_sessions')
    .update({ brand_id: targetId })
    .eq('id', sessionId);
  if (error) {
    throw new WorkspaceMoveError(isWorkspaceAssetError(error) ? 'workspace_asset_mismatch' : 'unknown', error);
  }
}
